import React, { useRef } from 'react'
import { EventContextProvider } from '../PostHog/index'
import { OracleLoadingSpinner } from '../OracleCommons'
import { useTranslatedText } from '../OracleCommons/StaticTextContext'
import { makeIcon } from '../OracleCommons/icons'
import { OracleModal } from './OracleModal'
import type { TarotSeedQuestion } from './hooks/useTarotSeedQuestions'
import type { TarotEntryCategory } from './TarotEntryScreen'

interface TarotCategoryModalProps {
  category: TarotEntryCategory | null
  categoryLabel: string
  isOpen: boolean
  onClose: () => void
  onSelectQuestion: (question: string) => void
  questions: TarotSeedQuestion[]
  loading: boolean
}

export const TarotCategoryModal: React.FC<TarotCategoryModalProps> = ({
  category,
  categoryLabel,
  isOpen,
  onClose,
  onSelectQuestion,
  questions,
  loading,
}) => {
  const hasBeenOpenedRef = useRef(false)
  if (isOpen) {
    hasBeenOpenedRef.current = true
  }

  const closeAriaLabel = useTranslatedText({ keys: ['tarotCategoryModal', 'closeAriaLabel'] })

  const handleSelectQuestion = (question: string) => {
    onSelectQuestion(question)
    onClose()
  }

  if (!hasBeenOpenedRef.current) {
    return null
  }

  // The spinner only renders during the initial fetch (no cached data yet).
  // Once the preload hook fills the cache, subsequent opens show the list
  // immediately even if a background refetch is in flight.
  const showSpinner = loading && questions.length === 0

  return (
    <EventContextProvider context="tarot">
      <OracleModal
        isOpen={isOpen}
        onDismiss={onClose}
        dismissOnTapOut
        className="tarot-category-modal"
      >
        <div className="tarot-category-modal__header">
          <h2 className="tarot-category-modal__title">{categoryLabel}</h2>
          <button
            type="button"
            className="tarot-category-modal__close-btn"
            aria-label={closeAriaLabel}
            onClick={() => onClose()}
          >
            {makeIcon('close')}
          </button>
        </div>
        <div className="tarot-category-modal__body">
          {showSpinner ? (
            <div
              className="tarot-category-modal__loading"
              data-testid="tarot-category-modal-loading"
            >
              <OracleLoadingSpinner />
            </div>
          ) : (
            <div className="tarot-category-modal__questions">
              {questions.map((q) => (
                <button
                  key={`${category}-${q.id}`}
                  type="button"
                  className="tarot-category-modal__question-btn"
                  onClick={() => handleSelectQuestion(q.question)}
                >
                  <span className="tarot-category-modal__question-text">{q.question}</span>
                  <span className="tarot-category-modal__question-send" aria-hidden="true">
                    {makeIcon('send-message', '18px')}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </OracleModal>
    </EventContextProvider>
  )
}
